import React, { useState } from 'react';
import { IAuditoriaBancariaResultado, IContaBancaria, IOcorrenciaBancaria } from '../interfaces/IAuditoriaBancaria';
import { formatCurrency, formatDate } from '../utils/formatters';

// COR-299: drill-down a nível de lançamento — a ocorrência diária de saldo credor precisa
// mostrar não só o saldo do dia, mas os lançamentos reais que o compuseram.
const BlocoLancamentosRelacionados: React.FC<{ ocorrencia: IOcorrenciaBancaria }> = ({ ocorrencia }) => {
  const lancamentos = ocorrencia.lancamentosRelacionados || [];

  if (lancamentos.length === 0) {
    return (
      <div className='text-muted mt-1' style={{ fontSize: 12 }}>
        Dia sem lançamento — saldo credor mantido desde o último movimento.
      </div>
    );
  }

  return (
    <div className='mt-2'>
      <strong className='fs-8'>Lançamentos do Dia ({lancamentos.length}):</strong>
      <div className='table-responsive mt-1'>
        <table className='table table-sm' style={{ fontSize: 12 }}>
          <thead>
            <tr>
              <th>Nº Lançamento</th>
              <th>Conta</th>
              <th>Histórico</th>
              <th className='text-end'>Débito</th>
              <th className='text-end'>Crédito</th>
            </tr>
          </thead>
          <tbody>
            {lancamentos.map((lanc, idx) => (
              <tr key={`${lanc.numeroLancamento}-${idx}`}>
                <td><code>{lanc.numeroLancamento || '-'}</code></td>
                <td><code>{lanc.contaContabil}</code></td>
                <td>{lanc.historico}</td>
                <td className='text-end'>{lanc.valorDebito > 0 ? formatCurrency(lanc.valorDebito) : '-'}</td>
                <td className='text-end'>{lanc.valorCredito > 0 ? formatCurrency(lanc.valorCredito) : '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

interface Props {
  resultado: IAuditoriaBancariaResultado;
}



const getIconeStatus = (status: string) => {
  switch (status) {
    case 'ERRO':
      return 'bi-exclamation-circle-fill';
    case 'ALERTA':
      return 'bi-exclamation-triangle-fill';
    case 'AVISO':
      return 'bi-info-circle-fill';
    default:
      return 'bi-check-circle-fill';
  }
};

const getLabelTipoConta = (tipo: string) => {
  switch (tipo) {
    case 'APLICACAO_LIQUIDEZ_IMEDIATA':
      return { label: 'Aplic. Liquidez', classe: 'auditoria-status status-AVISO' };
    default:
      return { label: 'Depósito Bancário', classe: 'auditoria-status status-CONCILIADO' };
  }
};

const AuditoriaSaldosBancarios: React.FC<Props> = ({ resultado }) => {
  const [contaExpandida, setContaExpandida] = useState<string | null>(null);

  const contas = resultado.contas || [];
  const depositos = contas.filter((c) => c.tipoConta !== 'APLICACAO_LIQUIDEZ_IMEDIATA');
  const aplicacoes = contas.filter((c) => c.tipoConta === 'APLICACAO_LIQUIDEZ_IMEDIATA');
  const contasComOcorrencias = contas.filter((c) => c.status !== 'CONCILIADO').length;

  const alternarExpandir = (chave: string) => {
    setContaExpandida(contaExpandida === chave ? null : chave);
  };

  return (
    <>
      <div className={`auditoria-alert status-${resultado.status}`} title={resultado.mensagemDirecionamento}>
        <i className={`bi ${getIconeStatus(resultado.status)} auditoria-alert-icon`} />
        <div>
          <strong>Saldos Bancários Invertidos — {resultado.status}</strong>
          <div>{resultado.mensagemDirecionamento}</div>
        </div>
      </div>

      <div className='auditoria-kpis'>
        <div className='auditoria-kpi'>
          <div className='auditoria-kpi-label'>
            {resultado.possuiAplicacoesLiquidezImediata
              ? 'Total de Disponibilidades Analisadas'
              : 'Total de Depósitos Bancários Analisados'}
          </div>
          <div className='auditoria-kpi-value'>{formatCurrency(resultado.totalDisponibilidadesAnalisadas)}</div>
          {resultado.possuiAplicacoesLiquidezImediata ? (
            <div className='auditoria-kpi-help'>
              Soma dos saldos (em módulo) de depósitos bancários (1.01.01.02) e aplicações de liquidez
              imediata (1.01.01.05): {formatCurrency(resultado.totalDepositosBancarios)} + {formatCurrency(resultado.totalAplicacoesLiquidezImediata)}.
            </div>
          ) : (
            <div className='auditoria-kpi-help'>
              Soma dos saldos (em módulo) das contas de depósito bancário (1.01.01.02) analisadas. Nenhuma
              conta de aplicação de liquidez imediata (1.01.01.05) foi identificada na ECD deste período.
            </div>
          )}
        </div>

        <div className={`auditoria-kpi ${resultado.penalidadePrevisivelTotal > 0 ? 'auditoria-kpi-critico' : 'auditoria-kpi-disabled'}`}>
          <div className='auditoria-kpi-label'>Penalidade Previsível Estimada</div>
          <div className='auditoria-kpi-value'>{formatCurrency(resultado.penalidadePrevisivelTotal)}</div>
          {resultado.penalidadePrevisivelTotal === 0 ? (
            <span className='auditoria-kpi-selo'>Zero Desperdício</span>
          ) : (
            <div className='auditoria-kpi-help'>Multa ECD potencial — 5% sobre o saldo credor de fechamento de cada conta, limitada a 1% da receita bruta do período, somada apenas para contas que fecharam com saldo credor.</div>
          )}
        </div>

        <div className='auditoria-kpi'>
          <div className='auditoria-kpi-label'>Dias com Saldo Credor</div>
          <div className='auditoria-kpi-value'>
            <span
              className='auditoria-contador auditoria-contador-erro'
              title='Dias com saldo credor no fechamento do período (ERRO)'
            >
              {resultado.totalErros}
            </span>{' '}
            <span
              className='auditoria-contador auditoria-contador-alerta'
              title='Dias com mais de 5 dias consecutivos de saldo credor (ALERTA)'
            >
              {resultado.totalAlertas}
            </span>{' '}
            <span
              className='auditoria-contador auditoria-contador-aviso'
              title='Dias com 1 a 5 dias consecutivos de saldo credor (AVISO)'
            >
              {resultado.totalAvisos}
            </span>
          </div>
          <div style={{ fontSize: '0.65rem', color: '#aaa', textAlign: 'center', marginTop: '-2px', letterSpacing: '0.05em' }}>
            ERRO · ALERTA · AVISO
          </div>
          <div className='auditoria-kpi-help'>
            Cada dia com saldo bancário negativo é contado individualmente — uma conta pode gerar múltiplos dias.
            {' '}AVISO: 1–5 dias consecutivos · ALERTA: mais de 5 · ERRO: saldo credor no fechamento.
            {contasComOcorrencias > 0 && (
              <>{' '}<strong>{contasComOcorrencias} conta{contasComOcorrencias !== 1 ? 's' : ''}</strong> com ocorrências na tabela abaixo.</>
            )}
          </div>
        </div>
      </div>

      <TabelaContas
        titulo='Depósitos Bancários (1.01.01.02)'
        descricao='Contas correntes e depósitos à vista — principal escopo da auditoria de saldo invertido.'
        contas={depositos}
        contaExpandida={contaExpandida}
        alternarExpandir={alternarExpandir}
        mensagemVazia='Nenhuma conta de depósito bancário (1.01.01.02) encontrada para os filtros selecionados.'
      />

      {/* COR-225: seção sempre exibida (mesmo vazia) — o usuário precisa ver explicitamente que
          aplicações de liquidez imediata foram consideradas no escopo da auditoria e, se nenhuma
          conta foi encontrada na ECD, por que o KPI acima não inclui essa categoria. */}
      <TabelaContas
        titulo='Aplicações Financeiras de Liquidez Imediata (1.01.01.05)'
        descricao='Contas de aplicação de liquidez imediata (Títulos e Valores Mobiliários — Não Hedge) — auditadas em categoria separada dos depósitos bancários.'
        contas={aplicacoes}
        contaExpandida={contaExpandida}
        alternarExpandir={alternarExpandir}
        mensagemVazia='Nenhuma conta de aplicação de liquidez imediata (1.01.01.05) foi identificada na ECD deste período — o total acima considera apenas depósitos bancários.'
      />
    </>
  );
};

interface TabelaContasProps {
  titulo: string;
  descricao: string;
  contas: IContaBancaria[];
  contaExpandida: string | null;
  alternarExpandir: (chave: string) => void;
  mensagemVazia: string;
}

const formatarPeriodoSequencia = (inicio: string | null, fim: string | null): string => {
  if (!inicio) return '-';
  const di = formatDate(inicio);
  const df = formatDate(fim ?? undefined);
  return di === df ? di : `${di} – ${df}`;
};

const TabelaContas: React.FC<TabelaContasProps> = ({ titulo, descricao, contas, contaExpandida, alternarExpandir, mensagemVazia }) => (
  <div className='auditoria-card'>
    <h3>{titulo}</h3>
    <p className='text-muted'>{descricao}</p>
    <p className='text-muted' style={{ fontSize: '0.85rem' }}>
      <i className='bi bi-calendar-range me-1' />
      <strong>Análise diária:</strong> o saldo bancário é reconstruído dia a dia a partir dos lançamentos da ECD.
      Qualquer dia com saldo credor (negativo) é registrado — inclusive quando o saldo final do período retorna a zero.
    </p>

    {contas.length === 0 && mensagemVazia && (
      <div className='text-muted mt-4'>{mensagemVazia}</div>
    )}

    {contas.length > 0 && (
      <div className='table-responsive mt-4'>
        <table className='auditoria-table'>
          <thead>
            <tr>
              <th>Banco / Conta</th>
              <th>Conta Contábil</th>
              <th>Referencial RFB</th>
              <th className='text-end'>Saldo Inicial</th>
              <th className='text-end'>Débitos</th>
              <th className='text-end'>Créditos</th>
              <th className='text-end'>Saldo Final do Período</th>
              <th className='text-center'>Dias com Saldo Credor</th>
              <th className='text-center'>Maior Sequência (dias)</th>
              <th className='text-center'>Período da Maior Sequência</th>
              <th className='text-end'>Maior Saldo Credor</th>
              <th className='text-end'>Penalidade Previsível</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {contas.map((conta) => {
              const chave = `${conta.empresaId}-${conta.contaContabil}`;
              const expandida = contaExpandida === chave;
              const { label: labelTipo, classe: classeTipo } = getLabelTipoConta(conta.tipoConta);
              const temSaldoCredorIntraPeriodo = conta.maiorSaldoCredor > 0;

              return (
                <React.Fragment key={chave}>
                  <tr className='auditoria-row-tributo' onClick={() => alternarExpandir(chave)}>
                    <td>
                      <div>{conta.banco}</div>
                      <small className={classeTipo} style={{ fontSize: '0.7rem' }}>{labelTipo}</small>
                      {conta.avisoQualidadeCadastral && (
                        <small
                          className='auditoria-status status-AVISO'
                          style={{ fontSize: '0.65rem', display: 'block', marginTop: '2px' }}
                          title={conta.avisoQualidadeCadastral}
                        >
                          <i className='bi bi-exclamation-diamond me-1' />
                          Divergência Cadastral
                        </small>
                      )}
                    </td>
                    <td>{conta.contaContabil}</td>
                    <td><small className='text-muted'>{conta.contaReferencial}</small></td>
                    <td className='text-end'>{formatCurrency(conta.saldoInicial)}</td>
                    <td className='text-end'>{formatCurrency(conta.totalDebitos)}</td>
                    <td className='text-end'>{formatCurrency(conta.totalCreditos)}</td>
                    <td className={`text-end ${conta.saldoFinalPeriodo < 0 ? 'auditoria-col-highlight' : ''}`}>
                      {formatCurrency(conta.saldoFinalPeriodo)}
                    </td>
                    <td className='text-center'>{conta.diasComSaldoCredor}</td>
                    <td className='text-center'>{conta.maiorSequenciaDiasConsecutivos}</td>
                    <td className='text-center'>
                      <small className={temSaldoCredorIntraPeriodo ? 'text-danger' : 'text-muted'}>
                        {formatarPeriodoSequencia(conta.dataInicioMaiorSequencia, conta.dataFimMaiorSequencia)}
                      </small>
                    </td>
                    <td className={`text-end ${temSaldoCredorIntraPeriodo ? 'auditoria-col-highlight' : ''}`}>
                      {temSaldoCredorIntraPeriodo ? formatCurrency(conta.maiorSaldoCredor) : '-'}
                    </td>
                    <td className='text-end'>{formatCurrency(conta.penalidadePrevisivel)}</td>
                    <td>
                      <span
                        className={`auditoria-status status-${conta.status}`}
                        title={conta.mensagemDiagnostico}
                      >
                        {conta.status}
                      </span>
                    </td>
                  </tr>

                  {expandida && (
                    <tr>
                      <td colSpan={13} style={{ background: '#fafbfc' }}>
                        <ContaOcorrencias conta={conta} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

const ContaOcorrencias: React.FC<{ conta: IContaBancaria }> = ({ conta }) => {
  const [ocorrenciaExpandida, setOcorrenciaExpandida] = useState<number | null>(null);

  return (
    <>
      {conta.avisoQualidadeCadastral && (
        <div
          className='auditoria-alert status-AVISO'
          style={{ marginBottom: '12px', padding: '8px 14px', borderRadius: '6px' }}
        >
          <i className='bi bi-exclamation-diamond-fill auditoria-alert-icon' style={{ fontSize: '1rem' }} />
          <div>
            <strong>Aviso de Qualidade Cadastral (I051):</strong>
            {' '}{conta.avisoQualidadeCadastral}
          </div>
        </div>
      )}

      {conta.justificativaStatus && (
        <div
          className={`auditoria-alert status-${conta.status}`}
          style={{ marginBottom: '12px', padding: '8px 14px', borderRadius: '6px' }}
        >
          <i className={`bi ${getIconeStatus(conta.status)} auditoria-alert-icon`} style={{ fontSize: '1rem' }} />
          <div>
            <strong>Critério de classificação — {conta.status}:</strong>
            {' '}{conta.justificativaStatus}
          </div>
        </div>
      )}

      {conta.penalidadePrevisivel > 0 && (
        <div className='text-muted' style={{ fontSize: 12, marginBottom: '12px' }}>
          Operações irregulares: {formatCurrency(conta.operacoesIrregularesEcd)} × 5% = {formatCurrency(conta.multaBruta5Ecd)}
          {' | '}Receita Bruta do Período: {conta.receitaBrutaPeriodoEcd != null ? formatCurrency(conta.receitaBrutaPeriodoEcd) : 'não disponível'}
          {conta.tetoLegal1Ecd != null && <> × 1% = {formatCurrency(conta.tetoLegal1Ecd)}</>}
          {' | '}Critério aplicado: {conta.criterioAplicadoEcd ?? conta.statusEvidenciaEcd}
        </div>
      )}

      {!conta.ocorrencias || conta.ocorrencias.length === 0 ? (
        <div className='text-muted py-2'>Nenhum dia com saldo credor detectado para esta conta.</div>
      ) : (
        <table className='auditoria-table'>
          <thead>
            <tr>
              <th>Data</th>
              <th className='text-end'>Saldo Bancário</th>
              <th className='text-center'>Dias Consecutivos</th>
              <th>Severidade</th>
              <th>Diagnóstico</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {conta.ocorrencias.map((ocorrencia, index) => {
              const expandida = ocorrenciaExpandida === index;
              return (
                <React.Fragment key={`${ocorrencia.dataEvento}-${index}`}>
                  <tr style={{ cursor: 'pointer' }} onClick={() => setOcorrenciaExpandida(expandida ? null : index)}>
                    <td>{formatDate(ocorrencia.dataEvento)}</td>
                    <td className='text-end'>{formatCurrency(ocorrencia.saldoBancario)}</td>
                    <td className='text-center'>{ocorrencia.diasConsecutivosCredor}</td>
                    <td>
                      <span className={`auditoria-status status-${ocorrencia.nivelSeveridade}`}>
                        {ocorrencia.nivelSeveridade}
                      </span>
                    </td>
                    <td>{ocorrencia.mensagemDiagnostico}</td>
                    <td className='text-end'>
                      <i className={`bi ${expandida ? 'bi-chevron-up' : 'bi-chevron-down'} text-muted`} />
                    </td>
                  </tr>
                  {expandida && (
                    <tr>
                      <td colSpan={6} style={{ background: '#fafbfc' }}>
                        <BlocoLancamentosRelacionados ocorrencia={ocorrencia} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      )}
    </>
  );
};

export default AuditoriaSaldosBancarios;
